import { useGetUserInfo } from 'auth';
import classNames from 'classnames';
import { ContractContributorResponse, ContractPermissionLevel } from 'compliance-api/generated';
import { useContext, useMemo, useState } from 'react';
import Badge from '@/modules/common/components/Badge.tsx';
import ContractContributorModal from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractContributorModal.tsx';
import {
  useContractContributorsQuery,
  useDeleteContractContributorMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  Avatar,
  Button,
  ConfirmModal,
  EmptyListState,
  ModalProviderContext,
  SearchField,
  Spinner,
} from 'ui';
import { EditGrayIcon, PlusIcon, TrashGrayIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

interface ContractByIDContributorsTabProps {
  contractId: number;
  readOnly: boolean;
}

const permissionBadgeType: Record<ContractPermissionLevel, 'info' | 'neutral' | 'warning'> = {
  [ContractPermissionLevel.Owner]: 'warning',
  [ContractPermissionLevel.Editor]: 'info',
  [ContractPermissionLevel.Viewer]: 'neutral',
};

export const ContractByIDContributorsTab = ({
  contractId,
  readOnly,
}: ContractByIDContributorsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { email: currentUserEmail } = useGetUserInfo();
  const [searchValue, setSearchValue] = useState('');
  const { mutateAsync: deleteContributor, isLoading: isDeleting } =
    useDeleteContractContributorMutation();

  const { data, isFetching, isLoading } = useContractContributorsQuery({ contractId }, !contractId);

  const contributors = useMemo(() => data?.contributors || [], [data?.contributors]);
  const currentContributor = useMemo(
    () =>
      contributors.find(contributor => {
        if (!currentUserEmail) return false;
        return contributor.email === currentUserEmail;
      }),
    [contributors, currentUserEmail],
  );
  const currentUserPermission = currentContributor?.permission_level;
  const canAddContributor =
    !readOnly &&
    (currentUserPermission === ContractPermissionLevel.Owner ||
      currentUserPermission === ContractPermissionLevel.Editor);
  const canManagePermissions = currentUserPermission === ContractPermissionLevel.Owner;

  const filteredContributors = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return contributors;

    return contributors.filter(contributor => {
      const haystack = [
        contributor.first_name,
        contributor.last_name,
        contributor.email,
        contributor.permission_level,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [contributors, searchValue]);

  const handleAddContributor = () => {
    openModal(ContractContributorModal, { contractId });
  };

  const handleEditContributor = (contributor: ContractContributorResponse) => {
    openModal(ContractContributorModal, { contractId, contributor });
  };

  const handleDeleteContributor = async (contributor: ContractContributorResponse) => {
    await deleteContributor({ contractId, contributorId: contributor.id });
  };

  if (isLoading && !data) {
    return (
      <div className="flex min-h-[180px] items-center justify-center">
        <Spinner size="md" variant="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SearchField
          placeholder="Search"
          value={searchValue}
          onChange={setSearchValue}
          allowClear
          className="w-full"
          isLoading={isFetching && !isLoading}
        />
        <div className="h-10 w-10 min-h-10 min-w-10">
          <Button
            type="button"
            className="h-10 w-10 min-w-0 px-0"
            onClick={handleAddContributor}
            aria-label="Add contributor"
            disabled={!canAddContributor}
          >
            <img src={PlusIcon} alt="" className="h-5 w-5 invert" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {filteredContributors.map(contributor => {
          const isOwner = contributor.permission_level === ContractPermissionLevel.Owner;
          const isCurrentUser = !!currentUserEmail && contributor.email === currentUserEmail;
          const isDeleteDisabled = !canManagePermissions || isOwner || isCurrentUser || isDeleting;
          const isEditDisabled = !canManagePermissions || isOwner || isCurrentUser;
          const contributorName = `${contributor.first_name || ''} ${
            contributor.last_name || ''
          }`.trim();

          return (
            <div
              key={contributor.id}
              className={classNames(
                'rounded border border-gray-200 bg-white px-4 py-3 transition-colors',
                'flex items-start justify-between gap-3 hover:bg-gray-50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <Avatar
                    src={contributor.avatar_url || undefined}
                    firstName={contributor.first_name}
                    lastName={contributor.last_name}
                    size="smMd"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-medium text-gray-900">
                        {contributorName || '-'}
                      </span>
                      <Badge type={permissionBadgeType[contributor.permission_level]}>
                        {fixNameEnum(contributor.permission_level)}
                      </Badge>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">{contributor.email || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleEditContributor(contributor)}
                  aria-label="Edit contributor"
                  disabled={isEditDisabled}
                >
                  <img src={EditGrayIcon} alt="" className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => {
                    if (isDeleteDisabled) return;
                    openModal(ConfirmModal, {
                      onConfirm: async () => {
                        await handleDeleteContributor(contributor);
                      },
                      title: (
                        <p>
                          Are you sure you want to remove{' '}
                          <span className="text-blue-700">
                            {contributorName || contributor.email || 'this contributor'}
                          </span>
                          ?
                        </p>
                      ),
                    });
                  }}
                  disabled={isDeleteDisabled || readOnly}
                  aria-label="Delete contributor"
                >
                  <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
                </button>
              </div>
            </div>
          );
        })}

        <EmptyListState
          title="No contributors linked to this contract"
          description="Add contributors to manage access and permissions for this contract."
          isLoading={false}
          isSearchUsed={!!searchValue}
          itemsCount={filteredContributors.length}
          classes={{
            container: 'min-h-[420px]',
            title: 'text-xl text-[28px]',
            image: 'h-20 w-20',
            description: 'text-sm',
          }}
          action={
            !canAddContributor
              ? undefined
              : {
                  content: 'Add Contributor',
                  onClick: handleAddContributor,
                }
          }
        />
      </div>
    </div>
  );
};
